// Per-property welcome guides: the host's own house manual and local guide,
// rendered at /stay/:slug/guide and fed (activities + favorites only) into
// the daily brief synthesis as host-verified recommendations.
//
// PRIVACY RULE (non-negotiable): never put door codes, lockbox codes, Wi-Fi
// passwords, alarm codes, or the street address in this file. This page is
// public. Those details travel in the booking platform's check-in message.
//
// hostFavoriteBenchIds is the per-host overlay on the shared city dining
// bench (public/data/dining-bench.json): ids the host personally recommends
// in their guide. The bench stays a city asset; favorites stay with the host.

export const guides = {
  'saltgrass-cottage': {
    hosts: 'James & Natalie',
    intro:
      "Welcome, and thank you for choosing our home for your stay in Mount Pleasant. Whether you're here for the historic streets of Charleston, the sand at Isle of Palms and Sullivan's, or a slow week on the porch, this guide collects everything we tell our guests: how the house works, where things live, and the places we actually go.",
    essentials: [
      {
        label: 'Check-in',
        value:
          'After 3:00 PM. Key pickup is by lockbox; the location and code arrive in your check-in message before arrival.'
      },
      { label: 'Check-out', value: 'By 10:00 AM. Please return the key to the lockbox.' },
      {
        label: 'Wi-Fi',
        value: 'Network name and password are in your check-in message.'
      },
      {
        label: 'Questions',
        value: "Message us anytime through the app you booked with, or email. You'll have our cell from booking to checkout."
      }
    ],
    locationHighlights: [
      { place: 'Isle of Palms', distance: '5.5 miles' },
      { place: "Sullivan's Island", distance: 'under 6 miles' },
      { place: 'Downtown Charleston', distance: 'under 7 miles' }
    ],
    rooms: [
      {
        name: 'Master bedroom',
        details: [
          'King bed with down duvet, cooling memory foam pillows, plush hotel-style pillows',
          'En suite bathroom (hair dryer under the vanity, black makeup towels)',
          'Pack & play and baby bedtime items in the closet',
          'Smart TV, bedside lamps with built-in chargers and touch controls'
        ]
      },
      {
        name: 'Guest bedroom, white queen',
        details: [
          'Queen bed with down duvet, cooling memory foam pillows, plush hotel-style pillows',
          'Smart TV, bedside lamps with charging ports, luggage rack in the closet'
        ]
      },
      {
        name: 'Guest bedroom, blue queen',
        details: [
          'Queen bed with down duvet, cooling memory foam pillows, plush hotel-style pillows',
          'Smart TV, bedside lamps with charging ports and touch controls'
        ]
      },
      {
        name: 'Guest bathroom',
        details: ['Hair dryer and extra black makeup towels under the vanity']
      },
      {
        name: 'Entertainment room',
        details: [
          'Queen pull-out couch (pillows and hypoallergenic duvet in the seagrass chest next to the TV console; sheets on the left side of the console table)',
          'Smart TV and a workstation with monitor',
          'Game and dining table (game top in the closet across from the laundry)',
          "Kids' books and toys on the right side of the console table"
        ]
      }
    ],
    whereToFind: [
      {
        category: 'Baby & toddler',
        items: [
          'Pack and play with sheet, bassinet insert, and optional toddler mattress (master bedroom closet)',
          'Sound machine (master bedroom closet)',
          'High chair (coat closet)',
          "Kids' plates, bowls, and cups (kitchen cabinet above the sink)"
        ]
      },
      {
        category: 'Kitchen',
        items: [
          'Crockpot, Ninja blender, and food processor (pantry by the window)',
          'Toaster, coffee maker, and K-cups on the counter',
          'Baking sheets (drawer under the oven), cutting boards (narrow cabinet right of the oven)'
        ]
      },
      { category: 'Safety', items: ['Fire extinguisher under the kitchen sink'] }
    ],
    houseRules: [
      'No smoking inside the home or on the screened-in porch',
      'No pets',
      'Only registered guests stay overnight',
      'No parties or events',
      'Quiet hours 10:00 PM to 8:00 AM'
    ],
    cameras:
      'One outdoor security camera over the driveway, video only. No indoor or private-area cameras.',
    checkoutList: [
      'Return the key to the lockbox',
      'Run the dishwasher with any dirty dishware',
      "Tidy up any major messes, we'll handle the rest"
    ],
    // The host's restaurant and activity picks, by category. Names that exist
    // in the city dining bench use the bench's exact spelling.
    localGuide: [
      {
        category: 'Breakfast & brunch',
        places: [
          { name: "Page's Okra Grill", area: 'Mount Pleasant', note: 'Southern classics, local favorite' },
          { name: 'Tavern & Table', area: 'Shem Creek', note: 'Waterfront brunch with a view' },
          { name: 'First Watch', area: 'Mount Pleasant', note: 'Fresh, healthy options' },
          { name: 'Sea Biscuit Café', area: 'Isle of Palms', note: 'Cozy, cash-only gem' },
          { name: "Jeff's Bagel Run", area: 'Mount Pleasant', note: 'Bagels and coffee to go, under a mile from the house' },
          { name: 'Mozzo Deli', area: 'Mount Pleasant', note: 'Breakfast sandwiches to go' },
          { name: 'Spread Bagelry', area: 'Mount Pleasant', note: 'Brick oven bagels and egg sandwiches' },
          { name: 'The Obstinate Daughter', area: "Sullivan's Island", note: 'Upscale brunch, also great for dinner' }
        ]
      },
      {
        category: 'Coffee',
        places: [
          { name: 'Vintage Coffee', area: 'Mount Pleasant', note: 'Stylish and relaxed' },
          { name: 'Metto Coffee', area: 'Mount Pleasant', note: 'Great espresso and lattes' },
          { name: 'Harken Café', area: 'Downtown', note: 'Beautiful space, excellent pastries' },
          { name: 'The Daily', area: 'Downtown', note: 'Trendy with breakfast bites' },
          { name: 'Second State Coffee', area: 'Downtown and Mount Pleasant', note: 'Modern shop with great beans' }
        ]
      },
      {
        category: 'Casual dining',
        places: [
          { name: 'Home Team BBQ', area: 'Multiple locations', note: 'Kid-friendly wings, nachos, and BBQ' },
          { name: "Poe's Tavern", area: "Sullivan's Island", note: 'Burgers with beach town vibes' },
          { name: 'San Miguel', area: 'Mount Pleasant', note: 'Mexican around the corner, under a mile from the house' },
          { name: 'Saltwater Cowboys', area: 'Shem Creek', note: 'High-energy, waterfront' },
          { name: "Leon's Oyster Shop", area: 'Downtown', note: 'Fried chicken and oysters' },
          { name: 'Taco Boy', area: 'Downtown', note: 'Colorful, festive spot' },
          { name: 'Locals', area: 'Mount Pleasant', note: 'Sushi and sports bar vibe' },
          { name: 'The Co-Op Frosé & Eatery', area: "Sullivan's Island", note: 'Sandwiches and frosé to go' },
          { name: 'Lewis Barbecue', area: 'Downtown', note: 'Top-rated Texas-style BBQ' },
          { name: "Edmund's Oast", area: 'Downtown', note: 'Excellent food, large covered patio' },
          { name: "Santi's", area: 'Mount Pleasant', note: 'Mexican with patio seating' },
          { name: 'The Wreck of the Richard and Charlene', area: 'Mount Pleasant', note: 'Come-as-you-are local seafood joint' }
        ]
      },
      {
        category: 'Dinner, reservations recommended',
        places: [
          { name: 'The Obstinate Daughter', area: "Sullivan's Island", note: 'Upscale coastal Italian' },
          { name: 'Halls Chophouse', area: 'Downtown', note: 'Classic steakhouse experience' },
          { name: '167 Raw', area: 'Downtown', note: 'Popular seafood spot, no reservations' },
          { name: "Melfi's", area: 'Downtown', note: 'Modern Italian' },
          { name: 'Tavern & Table', area: 'Shem Creek', note: 'Elegant waterside setting' },
          { name: 'O-Ku', area: 'Downtown', note: 'Stylish sushi bar' },
          { name: 'Frannie and the Fox', area: 'Downtown', note: 'Mediterranean small plates' },
          { name: 'Southbound', area: 'Downtown', note: 'Creative seasonal cuisine' }
        ]
      },
      {
        category: 'Drinks',
        places: [
          { name: 'Cocktail Club', area: 'Downtown', note: 'Intimate upstairs speakeasy' },
          { name: 'Citrus Club', area: 'Downtown', note: 'Rooftop with tropical views' },
          { name: "Red's Ice House", area: 'Shem Creek', note: 'Lively dockside bar' },
          { name: 'The Gin Joint', area: 'Downtown', note: 'Craft cocktails, hidden gem' },
          { name: "Coconut Joe's", area: 'Isle of Palms', note: 'Laid-back beach bar' }
        ]
      },
      {
        category: 'Desserts',
        places: [
          { name: "Carmella's Café & Dessert Bar", area: 'Downtown', note: 'Gelato, cakes, and cocktails' },
          { name: 'Ye Old Fashioned Ice Cream', area: 'Mount Pleasant', note: 'Classic diner-style treats' }
        ]
      }
    ],
    // Non-dining places, also fed to the brief synthesis as host-verified
    // picks for guests staying at this property.
    activities: [
      {
        category: 'Playgrounds & parks',
        places: [
          { name: 'Alhambra Playground', area: 'Mount Pleasant', note: 'Shaded, great for toddlers' },
          { name: 'Palmetto Islands County Park', area: 'Mount Pleasant', note: '$2 per person, wooded trails, playgrounds for toddlers and big kids' },
          { name: 'Mount Pleasant Memorial Waterfront Park', area: 'Mount Pleasant', note: 'Splash pad, playground, and the pier' },
          { name: 'Isle of Palms County Park', area: 'Isle of Palms', note: 'Beach access, rinse stations, small playground; parking $5 to $25 and fills early in season' },
          { name: 'Park Circle Playground', area: 'North Charleston', note: 'Huge inclusive playground for all ages' },
          { name: 'Hampton Park', area: 'Downtown', note: 'Flower gardens, walking paths, toddler playground nearby' }
        ]
      },
      {
        category: 'Rainy day with kids',
        places: [
          { name: 'AllwonderBILITY', area: 'Mount Pleasant', note: 'Indoor play' },
          { name: 'South Carolina Aquarium', area: 'Downtown', note: '' },
          { name: 'Sky Zone Trampoline Park', area: 'Mount Pleasant', note: '' },
          { name: "Children's Museum of the Lowcountry", area: 'Downtown', note: '' }
        ]
      },
      {
        category: 'Scenic walks',
        places: [
          { name: 'Pitt Street Bridge', area: 'Mount Pleasant', note: 'Serene marsh views' },
          { name: 'Shem Creek Boardwalk', area: 'Mount Pleasant', note: 'Marsh views, dolphin spotting' },
          { name: 'The Battery', area: 'Downtown', note: 'Waterfront mansions' },
          { name: 'Waterfront Park Pier', area: 'Downtown', note: 'Pineapple Fountain and harbor breezes' },
          { name: 'Rainbow Row and East Bay Street', area: 'Downtown', note: 'Colorful history' }
        ]
      },
      {
        category: 'Groceries',
        places: [
          { name: "Trader Joe's", area: 'Mount Pleasant', note: '1.3 miles from the house' },
          { name: 'The Fresh Market', area: 'Mount Pleasant', note: '1.8 miles from the house' },
          { name: 'Publix', area: 'Mount Pleasant', note: '2.2 miles from the house' },
          { name: 'Harris Teeter', area: 'Mount Pleasant', note: '2.3 miles from the house' }
        ]
      }
    ],
    // Bench ids this host personally recommends in their guide (overlay on
    // the shared city bench; see note at top of file).
    hostFavoriteBenchIds: [
      'pages-okra-grill',
      'tavern-and-table',
      'obstinate-daughter',
      'poes-tavern',
      'saltwater-cowboys',
      'reds-ice-house',
      'leons-oyster-shop',
      'lewis-barbecue',
      'edmunds-oast',
      'halls-chophouse',
      '167-raw',
      'co-op-sullivans',
      'wreck-of-richard-and-charlene'
    ]
  }
};

export function getGuide(slug) {
  return guides[slug] || null;
}
